import hash from 'hash-sum';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Alert, DialogRoot, toast } from '@gitbook/spine-react';
import { Logger, sanitizeError } from '@gitbook/util-debug';
import { useEventCallback, useLiveRef, useStateWithDeps } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import type { ContentKitRemote, ContentKitRemoteInput } from './cache';
import {
    ContentKitElement,
    type ContentKitLocalState,
    type ContentKitRemoteComponents,
    type ContentKitSecurity,
    type ContentKitVisitorContextData,
    resolveDynamicBinding,
} from './elements';

const logger = Logger('contentkit:lifecycle');

export interface ContentKitLifecycleRef {
    dispatchAction: (action: api.ContentKitAction) => Promise<void>;
}

export interface ContentKitLifecycleProps {
    /**
     * Expected output to render.
     */
    mode: api.ContentKitRenderOutputElement['element']['type'];

    /**
     * Interface to fetch remote data.
     */
    remote: ContentKitRemote;

    /**
     * Security restrictions
     */
    security: ContentKitSecurity;

    /**
     * Component to render.
     */
    input: ContentKitRemoteInput;

    /**
     * Remote components, injected from outside.
     */
    remoteComponents: ContentKitRemoteComponents;

    /**
     * Visitor context data for elements that can receive visitor state.
     */
    visitorContext?: ContentKitVisitorContextData;

    /**
     * Callback when the props of the block have been updated.
     * If not defined, the props can't be updated.
     */
    onPropsChange?: (props: object) => Promise<void>;

    /**
     * Callback when an action is executed in this component.
     */
    onAction?: (action: api.ContentKitAction) => void;

    /**
     * Callback when the component has completed its lifecycle, i.e when there is no element to
     * render.
     */
    onComplete?: (value: api.ContentKitRenderOutputComplete['returnValue']) => void;
}

/**
 * Render a contenkit component from remote and handle its lifecycle.
 */
export function ContentKitLifecycle({
    ref,
    ...props
}: ContentKitLifecycleProps & {
    ref?: React.Ref<ContentKitLifecycleRef>;
}): React.ReactElement | null {
    const { mode, remote, input } = props;

    const [currentInput, setCurrentInput] = useStateWithDeps(input, [input]);
    const { result: currentValue, error } = readRemote(remote, mode, currentInput);

    return error ? (
        <ContentKitLifecycleOutputError error={error} mode={mode} />
    ) : currentValue.type === 'complete' ? (
        <ContentKitLifecycleOutputComplete {...props} currentValue={currentValue} />
    ) : (
        <ContentKitLifecycleOutputElement
            {...props}
            ref={ref}
            currentInput={currentInput}
            setCurrentInput={setCurrentInput}
            currentValue={currentValue}
        />
    );
}

/**
 * Render a contenkit component `element` output.
 */
function ContentKitLifecycleOutputElement({
    ref,
    ...props
}: (ContentKitLifecycleProps & {
    currentInput: ContentKitRemoteInput;
    setCurrentInput: React.Dispatch<React.SetStateAction<ContentKitRemoteInput>>;
    currentValue: api.ContentKitRenderOutputElement;
}) & { ref?: React.Ref<ContentKitLifecycleRef> }): React.ReactElement {
    const {
        remote,
        input,
        security,
        onPropsChange,
        onAction,
        currentInput,
        setCurrentInput,
        currentValue,
        onComplete,
    } = props;

    const [subView, setSubView] = React.useState<null | {
        mode: ContentKitLifecycleProps['mode'];
        input: ContentKitRemoteInput;
    }>(null);

    const [localState, setLocalState] = useStateWithDeps<ContentKitLocalState>(
        currentValue.state || {},
        [currentValue]
    );

    const transitionTo = async (partial: Partial<ContentKitRemoteInput>) => {
        let newInput = {
            ...currentInput,
            ...(currentValue
                ? {
                      props: onPropsChange ? currentValue.props : currentInput.props,
                      state: currentValue.state,
                  }
                : {}),
            ...partial,
            state: {
                ...currentInput.state,
                ...currentValue?.state,
                ...localState,
                ...partial.state,
            },
        };

        const result = await remote.readAsync(newInput);

        // When output of an action is of type `complete`, we immediately complete the lifecycle and do not wait for the next rendering
        // as the input passed to the component could be changed as a side-effect of this action
        // Ex: when an integration updates an installation configuration in an action, while return type: 'complete' at the end.
        if (result.type === 'complete') {
            logger.debug('lifecycle complete', result.returnValue);
            onComplete?.(result.returnValue);
            return;
        }

        // Any action may update the state or props of the component. Handle that here.
        if (result.type === 'element') {
            newInput = {
                ...newInput,
                props: onPropsChange
                    ? {
                          ...newInput.props,
                          ...result.props,
                      }
                    : newInput.props,
                state: {
                    ...newInput.state,
                    ...result.state,
                },
            };

            // The two inputs are equivalent in terms of result, so we can write cache here.
            remote.write(newInput, result);

            if (onPropsChange && hash(currentValue.props) !== hash(newInput.props)) {
                logger.info('transition caused a props change', newInput.props);
                onPropsChange(newInput.props);
            }
        }

        logger.info('transition to', newInput);
        setCurrentInput(newInput);
    };

    const dispatchAction = async (actionInput: api.ContentKitAction, bubble = true) => {
        const action = resolveDynamicBinding<api.ContentKitAction>(localState, actionInput);

        logger.debug('dispatch action', action);

        if (bubble) {
            onAction?.(action);
        }

        switch (action.action) {
            case '@ui.modal.open': {
                const modalInput: ContentKitRemoteInput = {
                    integrationId: input.integrationId,
                    componentId: action.componentId,
                    props: action.props,
                    context: input.context,
                    action,
                };

                await toast.catch(async () => {
                    // Prefetch the modal content to show a loading in the button opening the button
                    await remote.readAsync(modalInput);
                    setSubView({ mode: 'modal', input: modalInput });
                }, 'Error while opening dialog');
                break;
            }
            case '@ui.modal.close': {
                // this action only executes in the subview
                break;
            }

            case '@ui.url.open':
                window.open(action.url, '_blank');
                break;

            case '@editor.node.updateProps':
                logger.info('update props from action', action.props);
                await onPropsChange?.(action.props);
                break;

            default: {
                await toast.catch(
                    () =>
                        transitionTo({
                            action,
                        }),
                    'Error while executing action'
                );
                break;
            }
        }
    };

    React.useImperativeHandle(ref, () => ({
        dispatchAction,
    }));

    const onPropsChangeRef = useLiveRef(onPropsChange);

    // handle props change from the parent
    React.useEffect(() => {
        if (hash(currentValue.props) !== hash(currentInput.props)) {
            logger.info('props have changed', currentValue.props);
            onPropsChangeRef.current?.(currentValue.props);
        }
    }, [currentInput, currentValue, onPropsChangeRef]);

    const onSubViewAction = useEventCallback((action: api.ContentKitAction) => {
        switch (action.action) {
            case '@ui.modal.close':
                transitionTo({
                    action,
                });
                setSubView(null);
                break;
        }
    });

    const onDialogOpenChange = useEventCallback((isOpen: boolean) => {
        if (!isOpen) {
            // When the modal is closed from the overlay, we just close the subview without
            // dispatching an action - this mimics a real "cancel" event where the user doesn't
            // want the changes they made in the modal to persist.
            setSubView(null);
        }
    });

    const visitorContext = currentValue.canAccessVisitorClaims ? props.visitorContext : undefined;

    return (
        <>
            <ContentKitElement
                element={currentValue.element}
                localState={localState}
                setLocalState={setLocalState}
                dispatchAction={dispatchAction}
                security={security}
                remoteComponents={props.remoteComponents}
                visitorContext={visitorContext}
            />
            <DialogRoot isOpen={Boolean(subView)} onOpenChange={onDialogOpenChange}>
                <React.Suspense fallback={null}>
                    {subView ? (
                        <ContentKitLifecycle
                            remote={remote}
                            mode={subView.mode}
                            input={subView.input}
                            onAction={onSubViewAction}
                            security={security}
                            remoteComponents={props.remoteComponents}
                            visitorContext={props.visitorContext}
                        />
                    ) : null}
                </React.Suspense>
            </DialogRoot>
        </>
    );
}

/**
 * Render a contenkit component `complete` output.
 */
function ContentKitLifecycleOutputComplete(
    props: {
        currentValue: api.ContentKitRenderOutputComplete;
    } & ContentKitLifecycleProps
) {
    const { currentValue, onComplete } = props;

    // handle component completion
    React.useLayoutEffect(() => {
        if (!onComplete) {
            logger.warning('No completion callback provided for this component');
        }

        onComplete?.(currentValue.returnValue);
    }, [currentValue, onComplete]);

    return null;
}

/**
 * Render a contenkit component `error` output.
 */
function ContentKitLifecycleOutputError(props: {
    error: Error;
    mode: ContentKitLifecycleProps['mode'];
}) {
    const { error, mode } = props;

    switch (mode) {
        case 'configuration':
        case 'block':
            return (
                <Alert
                    kind="danger"
                    details={sanitizeError(
                        error,
                        'Oops! unexpected error while rendering the integration'
                    )}
                />
            );
        case 'modal':
            return null;
        default:
            assertNever(mode);
    }
}

function readRemote(
    remote: ContentKitRemote,
    mode: ContentKitLifecycleProps['mode'],
    input: ContentKitRemoteInput
) {
    const { result, error } = remote.readSafe(input);

    if (error) {
        return { error: error || new Error('Unknown error') };
    }

    if (result.type === 'element') {
        // IMPORTANT!
        // Skip any further checks about output mode and expected mode.
        // This is done to support legacy integration configuration components that might
        // yet be wrapped it with <configuration>
        if (mode === 'configuration') {
            if (result.element.type !== 'configuration') {
                logger.warning(
                    `Unexpected output element type, expected configuration, got ${result.element.type}. Configuration components should be wrapped in <configuration> tag.`
                );
            }

            return { result };
        }

        if (result.element.type !== mode) {
            return {
                error: new Error(
                    `Unexpected output element type, expected ${mode}, got ${result.element.type}`
                ),
            };
        }
    }

    return { result };
}
